import { MoreVert } from '@mui/icons-material';
import { Paper } from '@mui/material';
import {
  GridActionsCellItem,
  GridColDef,
  GridPaginationModel,
} from '@mui/x-data-grid';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLazyGetDeliverablesQuery } from '../../../../app/athenaApi';
import { HandlersDeliverableResponse } from '../../../../app/athenaApiGen';
import {
  setPage,
  setPageSize,
  setSelectedRowId,
  setViewPanel,
} from '../../../../app/deliverables/deliverablesSlice';
import { RootState } from '../../../../app/store';
import CustomNoRows from '../../../../components/DataGrid/CustomNoRows';
import NoPermissionsRows from '../../../../components/DataGrid/NoPermissionsRows';
import { StyledDataGrid } from '../../../../shared-theme/StyledComponents';
import useDebounce from '../../../../useDebounce';
import { TABLE_PAGE_SIZE_OPTIONS } from '../../../../utils/tableVariables';
import {
  getDeliverableState,
  getDeliverableType,
  isActionEnabled,
} from '../../../utilities';
import { deliverableColumns } from '../../consts';
import ActionsMenu from './components/ActionsMenu';
import { DeliverablesColumnMenu } from './DeliverablesColumnMenu';

type TDeliverablesProps = {
  readOnly: boolean;
  workflowState: string[];
};

declare module '@mui/x-data-grid' {
  interface BasePaginationPropsOverrides {
    showFirstButton: boolean;
    showLastButton: boolean;
  }
}

const DeliverablesTable = (props: TDeliverablesProps) => {
  const dispatch = useDispatch();
  const [queryDeliverables, { data, isLoading }] =
    useLazyGetDeliverablesQuery();
  const {
    page,
    pageSize,
    isViewPanelOpen,
    selectedRowId,
    allowedActions,
    projectIdFilter,
    typeIdFilter,
    authorIdFilter,
  } = useSelector((state: RootState) => state.deliverables);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openMenu, setOpenMenu] = useState<boolean>(false);

  const [columns, setColumns] =
    useState<GridColDef<HandlersDeliverableResponse>[]>(deliverableColumns);

  const rowCountRef = useRef(data?.total || 0);

  const rowCount = useMemo(() => {
    if (data?.total !== undefined) {
      rowCountRef.current = data.total;
    }
    return rowCountRef.current;
  }, [data?.total]);

  const isDraftViewEnabled = isActionEnabled(
    allowedActions,
    'list-inflight-deliverables',
  );
  const isSubmittedAndCompletedViewEnabled = isActionEnabled(
    allowedActions,
    'list-submitted-deliverables',
  );

  const showNoPermissions =
    props.workflowState.length === 0
      ? !isDraftViewEnabled && !isSubmittedAndCompletedViewEnabled
      : props.workflowState.find((state) => state.includes('Pending'))
        ? !isDraftViewEnabled
        : props.workflowState.find(
              (state) =>
                state.includes('Review') ||
                state.includes('Approved') ||
                state.includes('Rejected'),
            )
          ? !isSubmittedAndCompletedViewEnabled
          : false;

  const handlePaginationChange = (props: GridPaginationModel) => {
    dispatch(setPage(props.page));
    dispatch(setPageSize(props.pageSize));
  };

  const debouncePagination = useDebounce(handlePaginationChange, 300);

  const fetchDeliverables = useCallback(async () => {
    try {
      await queryDeliverables({
        page: page + 1,
        limit: pageSize,
        workflowState: props.workflowState,
        projectId: projectIdFilter || undefined,
        deliverableTypeId: typeIdFilter || undefined,
        authorId: authorIdFilter || undefined,
      }).unwrap();
    } catch (err) {
      console.log(err);
    }
  }, [
    page,
    pageSize,
    queryDeliverables,
    props.workflowState,
    projectIdFilter,
    typeIdFilter,
    authorIdFilter,
  ]);

  useEffect(() => {
    if (!showNoPermissions) {
      fetchDeliverables();
    }
  }, [fetchDeliverables, showNoPermissions]);

  useEffect(() => {
    if (!props.readOnly) {
      setColumns((prev) => [
        ...prev,
        {
          field: 'actions',
          type: 'actions',
          headerName: 'Actions',
          width: 100,
          cellClassName: 'actions',
          getActions: () => {
            return [
              <GridActionsCellItem
                icon={<MoreVert />}
                label="Actions"
                id="deliverable-action-button"
                onClick={(e) => {
                  setAnchorEl(e.currentTarget);
                  setOpenMenu(true);
                }}
                color="inherit"
              />,
            ];
          },
        },
      ]);
    }
  }, []);

  return (
    <Paper sx={{ width: '100%', height: '100%', overflow: 'hidden' }}>
      <StyledDataGrid
        paginationMode="server"
        pagination
        pageSizeOptions={TABLE_PAGE_SIZE_OPTIONS}
        loading={isLoading}
        hideFooterPagination={showNoPermissions}
        getRowClassName={(params) =>
          params.id === selectedRowId ? 'selected-project-row' : ''
        }
        onCellClick={({ id, colDef }) => {
          dispatch(setSelectedRowId(id.toString()));
          const delieverable = data?.deliverables?.find(
            (del) => del.deliverableId === id.toString(),
          );
          const deliverableType = getDeliverableType(
            delieverable?.deliverableType?.abbreviation ?? '',
          );
          const state = getDeliverableState(delieverable?.workflowState ?? '');
          const isViewEnabled = isActionEnabled(
            allowedActions,
            `get-${state}-${deliverableType}`,
          );
          //short the function, because we do not need to explicitly open the view panel on a click in the action column
          //we cannot allow to view the deliverable, if the action isn't available
          if (colDef.field === 'actions' || !isViewEnabled) return;

          if (id === selectedRowId) {
            dispatch(setViewPanel(!isViewPanelOpen));
          } else if (!isViewPanelOpen) {
            dispatch(setViewPanel(true));
          }
        }}
        initialState={{
          // paginationMode is "server", so this only sorts the current
          // page's rows. A true cross-page default sort needs a backend
          // `sort` query param on /api/deliverables.
          sorting: {
            sortModel: [{ field: 'dateSubmitted', sort: 'desc' }],
          },
        }}
        paginationModel={{
          page: page,
          pageSize: pageSize,
        }}
        slotProps={{
          basePagination: {
            showFirstButton: true,
            showLastButton: true,
          },
        }}
        onPaginationModelChange={debouncePagination}
        rowCount={rowCount}
        rows={showNoPermissions ? [] : (data?.deliverables ?? [])}
        getRowId={(del) => del.deliverableId ?? ''}
        columns={columns}
        slots={
          showNoPermissions
            ? {
                noRowsOverlay: NoPermissionsRows,
                noResultsOverlay: NoPermissionsRows,
              }
            : {
                noRowsOverlay: CustomNoRows,
                columnMenu: DeliverablesColumnMenu,
              }
        }
      />

      <ActionsMenu
        anchorEl={anchorEl}
        openMenu={openMenu}
        workflowState={props.workflowState}
        handleClose={() => {
          setOpenMenu(false);
          setAnchorEl(null);
        }}
      />
    </Paper>
  );
};

export default DeliverablesTable;
